/**
 * Decision rights enforcement — resolving who may actually exercise a right.
 *
 * `types/prioritization/decisionRights.ts` supplies the pure primitives:
 * `resolveDecisionRight` (which right governs this decision) and
 * `checkDecisionRight` (does this user match the right's OWNER binding). This
 * module adds the two things enforcement needs that those do not cover:
 *
 *  1. **Delegations.** `checkDecisionRight` answers only for the owner. A user
 *     holding an accepted, unexpired, unrevoked delegation is equally entitled
 *     to act, within the modes that delegation carries — and `checkDecisionRight`
 *     would deny them. `checkCanDelegate` governs *creating* a delegation, not
 *     acting under one; nothing resolved an active delegation into an
 *     entitlement before this module.
 *
 *  2. **The unconfigured case.** A cycle with no decision rights rows must
 *     behave exactly as it did before decision rights existed. Enforcement
 *     therefore FAILS OPEN when nothing is configured and FAILS CLOSED once it
 *     is — which is what makes enforcement shippable onto live cycles without
 *     locking anyone out of their own work.
 *
 * Everything here is pure. No DB access, no store reads — callers load the
 * configuration (see `useDecisionRightGate`) and pass it in, so this is
 * testable without a database and reusable from a server context.
 *
 * NOTE ON TRUST: this is the client-side affordance — it decides what the UI
 * offers and explains. It is not the control. The authority itself is enforced
 * by the RPCs that commit each outcome; this module must never be the only
 * thing standing between a user and a governed write.
 */

import {
  checkDecisionRight,
  resolveDecisionRight,
  type DecisionExecutionMode,
  type DecisionRight,
  type DecisionRightsConfiguration,
  type DelegatedDecisionRight,
} from '../types/prioritization/decisionRights';
import type {
  CanonicalSessionType,
  CanonicalDecisionClassKind,
} from '../types/prioritization/session';

// ============================================================================
// Result
// ============================================================================

/** How a user came to hold the right they are exercising. */
export type DecisionRightHolderKind = 'owner' | 'delegate' | 'unconfigured';

export interface DecisionRightEvaluation {
  /** Whether the user may exercise this decision in the requested mode. */
  allowed: boolean;
  /**
   * Whether any right governs this decision at all. When false, `allowed` is
   * true by the fail-open rule and no right was consulted — the caller should
   * present no governance affordance rather than claiming authority.
   */
  configured: boolean;
  /** How the entitlement arose. 'unconfigured' when no right governs. */
  holder: DecisionRightHolderKind;
  /** Human-readable denial reason, suitable for display. Absent when allowed. */
  reason?: string;
  /** The right that governed the decision, when one did. */
  right?: DecisionRight;
  /** The delegation the user acted under, when they acted as a delegate. */
  delegation?: DelegatedDecisionRight;
  /** Whether this user may delegate the right onward. */
  canDelegate: boolean;
  /** Whether this user may reassign the right (meta-assignment authority). */
  canReassign: boolean;
}

export interface DecisionRightRequest {
  domain: CanonicalSessionType;
  decisionClass: CanonicalDecisionClassKind;
  /** Defaults to '*'. A scoped right takes precedence over the wildcard. */
  scopeSelector?: string;
  /** The mode the user is attempting to act in. */
  mode: DecisionExecutionMode;
  userId: string;
  /** Role/capability ids the user holds in the cycle's scope. */
  userRoleIds: string[];
  /** Evaluation time; injectable so expiry is testable. Defaults to now. */
  now?: Date;
}

// ============================================================================
// Delegation resolution
// ============================================================================

/**
 * Is this delegation live for the given user right now?
 *
 * A delegation grants nothing unless it has been accepted (delegations created
 * under `requiresAcceptance` start unaccepted), has not been revoked, and has
 * not expired. An absent `expiresAt` means "for the duration of the cycle",
 * not "forever" — the cycle bounds it.
 */
export function isDelegationActive(
  delegation: DelegatedDecisionRight,
  userId: string,
  now: Date = new Date()
): boolean {
  if (delegation.delegatePrincipalId !== userId) return false;
  if (delegation.revoked) return false;
  if (!delegation.accepted) return false;
  if (delegation.expiresAt && new Date(delegation.expiresAt).getTime() <= now.getTime()) {
    return false;
  }
  return true;
}

/**
 * Finds the live delegation, if any, granting `userId` the given right.
 * When several are live (re-delegation at different depths), the shallowest
 * wins — it is the one closest to the original holder.
 */
export function findActiveDelegation(
  config: DecisionRightsConfiguration,
  rightId: string,
  userId: string,
  now: Date = new Date()
): DelegatedDecisionRight | undefined {
  return config.delegations
    .filter((d) => d.sourceRightId === rightId && isDelegationActive(d, userId, now))
    .sort((a, b) => a.depth - b.depth)[0];
}

// ============================================================================
// Evaluation
// ============================================================================

/**
 * Decides whether a user may exercise a decision, given the cycle's configured
 * rights.
 *
 * Order of resolution:
 *   1. No configuration, or no right governs this (domain, class, scope) →
 *      ALLOW, `configured: false`. Pre-decision-rights behaviour, unchanged.
 *   2. The user matches the right's owner binding → the owner path, delegated
 *      to `checkDecisionRight` so mode validation stays in one place.
 *   3. The user holds a live delegation on that right → allowed if the
 *      requested mode is within the delegation's modes.
 *   4. Otherwise → DENY, with the reason from the owner check.
 *
 * A delegate never inherits meta-assignment authority: reassigning a right is
 * bound separately and is not something a delegation can confer.
 */
export function evaluateDecisionRight(
  config: DecisionRightsConfiguration | null | undefined,
  request: DecisionRightRequest
): DecisionRightEvaluation {
  const { domain, decisionClass, scopeSelector, mode, userId, userRoleIds } = request;
  const now = request.now ?? new Date();

  const unconfigured: DecisionRightEvaluation = {
    allowed: true,
    configured: false,
    holder: 'unconfigured',
    canDelegate: false,
    canReassign: false,
  };

  if (!config) return unconfigured;

  const right = resolveDecisionRight(config, domain, decisionClass, scopeSelector);
  if (!right) return unconfigured;

  const context = { userId, userRoleIds };
  const ownerCheck = checkDecisionRight(right, context, mode);

  if (ownerCheck.allowed) {
    return {
      allowed: true,
      configured: true,
      holder: 'owner',
      right,
      canDelegate: ownerCheck.canDelegate,
      canReassign: ownerCheck.canReassign,
    };
  }

  // Not the owner — or the owner in a mode this right does not permit. A
  // delegation can supply the former but never the latter: a delegate's modes
  // are already constrained to a subset of the source right's at creation.
  const delegation = findActiveDelegation(config, right.id, userId, now);
  if (delegation) {
    if (delegation.allowedExecutionModes.includes(mode)) {
      return {
        allowed: true,
        configured: true,
        holder: 'delegate',
        right,
        delegation,
        // Re-delegation is governed by the source right's maxDepth and is
        // checked by checkCanDelegate at creation time, not here.
        canDelegate: false,
        canReassign: false,
      };
    }
    return {
      allowed: false,
      configured: true,
      holder: 'delegate',
      right,
      delegation,
      reason:
        `Your delegation for this decision does not permit '${mode}'. ` +
        `Permitted: ${delegation.allowedExecutionModes.join(', ')}.`,
      canDelegate: false,
      canReassign: false,
    };
  }

  return {
    allowed: false,
    configured: true,
    holder: 'owner',
    right,
    reason: ownerCheck.reason,
    canDelegate: ownerCheck.canDelegate,
    canReassign: ownerCheck.canReassign,
  };
}

/**
 * Every user currently entitled to exercise a right — the owner binding
 * described in words, plus any live delegates. Used to render "who holds this"
 * without claiming a single answer when a role binding covers several people.
 */
export function describeRightHolder(right: DecisionRight): string {
  switch (right.owner.bindingType) {
    case 'PRINCIPAL_IN_SCOPE':
      return right.owner.principalId ?? 'Unassigned';
    case 'ROLE_IN_SCOPE':
      return right.owner.roleId ? `Anyone with ${right.owner.roleId}` : 'Unassigned role';
    case 'ROLE_AND_PRINCIPAL':
      return right.owner.principalId && right.owner.roleId
        ? `${right.owner.principalId} (as ${right.owner.roleId})`
        : 'Incompletely bound';
  }
}
